import { Select, Space } from 'antd'; // Import Ant Design components
import React, { FC, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  DomotzSpeedTest,
  DomotzSpeedTestEntry,
} from '../../../interfaces/NetworkDevices';
import Typography from '../../typography/Typography';

const CHART_THEMES: Record<string, any> = {
  cyberpunk: {
    download: { stroke: '#06b6d4', fill: '#06b6d4', stopOpacity: 0.3 },
    upload: { stroke: '#d946ef', fill: '#d946ef', stopOpacity: 0.3 },
    grid: 'rgba(156, 163, 175, 0.15)',
    text: '#9ca3af',
  },
  enterprise: {
    download: { stroke: '#6366f1', fill: '#6366f1', stopOpacity: 0.2 },
    upload: { stroke: '#94a3b8', fill: '#94a3b8', stopOpacity: 0.15 },
    grid: 'rgba(0, 0, 0, 0.05)',
    text: '#475569',
  },
  nature: {
    download: { stroke: '#10b981', fill: '#10b981', stopOpacity: 0.25 },
    upload: { stroke: '#a7f3d0', fill: '#a7f3d0', stopOpacity: 0.2 },
    grid: 'rgba(16, 185, 129, 0.05)',
    text: '#6b7280',
  },
  sunset: {
    download: { stroke: '#f59e0b', fill: '#f59e0b', stopOpacity: 0.3 },
    upload: { stroke: '#f43f5e', fill: '#f43f5e', stopOpacity: 0.2 },
    grid: 'rgba(245, 158, 11, 0.1)',
    text: '#f59e0b',
  },
  minimalist: {
    download: { stroke: '#18181b', fill: '#18181b', stopOpacity: 0.1 },
    upload: { stroke: '#a1a1aa', fill: '#a1a1aa', stopOpacity: 0.05 },
    grid: 'rgba(0, 0, 0, 0.03)',
    text: '#a1a1aa',
  },
};

const BITS_PER_MB = 1000000;
const bpsToMbpsFormatter = (bps: number) => (bps / BITS_PER_MB).toFixed(1);

const NetworkSpeedTestHistory: FC<DomotzSpeedTest> = ({ history }) => {
  const [themeKey, setThemeKey] = useState<string>('sunset');
  const activeTheme = CHART_THEMES[themeKey];

  const processedData = useMemo(() => {
    return history.map((item: DomotzSpeedTestEntry) => ({
      // Localized for Belgium (24h format, Europe/Brussels timezone)
      time: new Date(item.timestamp).toLocaleString('en-BE', {
        timeZone: 'Europe/Brussels',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // Belgium typically uses 24h clock
      }),
      download: item.values[0],
      upload: item.values[1],
    }));
  }, [history]);

  const themeOptions = Object.keys(CHART_THEMES).map((key) => ({
    label: key.charAt(0).toUpperCase() + key.slice(1),
    value: key,
  }));

  return (
    <div className="mt-8 w-full p-6 antialiased bg-white dark:bg-gray-900 rounded-lg border dark:border-gray-700 shadow-sm transition-colors duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
        <div>
          <Typography tag="h2" type="subtitle">
            Bandbreedte
          </Typography>
          <Typography tag="p" type="label">
            Geschiedenis van: Upload- en downloadsnelheid (in Mbps, geschat)
          </Typography>
        </div>

        <Space size="middle">
          <div className="hidden sm:flex gap-4 p-1 rounded-lg border dark:border-gray-700">
            <LegendItem color={activeTheme.download.stroke} label="Download" />
            <LegendItem color={activeTheme.upload.stroke} label="Upload" />
          </div>

          <Select
            value={themeKey}
            onChange={setThemeKey}
            style={{ width: 140 }}
            options={themeOptions}
            className="modern-select"
            popupClassName="rounded-xl shadow-2xl"
          />
        </Space>
      </div>

      <div className="h-[350px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={processedData}
            margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="downGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={activeTheme.download.fill}
                  stopOpacity={activeTheme.download.stopOpacity}
                />
                <stop
                  offset="95%"
                  stopColor={activeTheme.download.fill}
                  stopOpacity={0}
                />
              </linearGradient>
              <linearGradient id="upGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={activeTheme.upload.fill}
                  stopOpacity={activeTheme.upload.stopOpacity}
                />
                <stop
                  offset="95%"
                  stopColor={activeTheme.upload.fill}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              stroke={activeTheme.grid}
            />

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'currentColor', fontWeight: 600 }}
              dy={10}
              minTickGap={40}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'currentcolor', fontWeight: 600 }}
              tickFormatter={(val) => `${(val / BITS_PER_MB).toFixed(0)}`}
            />

            <Tooltip
              content={<CustomTooltip activeTheme={activeTheme} />}
              cursor={{ stroke: activeTheme.text, strokeWidth: 0.5 }}
            />

            <Area
              name="Download"
              type="monotone"
              dataKey="download"
              stroke={activeTheme.download.stroke}
              strokeWidth={2.5}
              fill="url(#downGrad)"
              activeDot={{
                r: 4,
                strokeWidth: 0,
                fill: activeTheme.download.stroke,
              }}
              animationDuration={800}
            />

            <Area
              name="Upload"
              type="monotone"
              dataKey="upload"
              stroke={activeTheme.upload.stroke}
              strokeWidth={2.5}
              fill="url(#upGrad)"
              activeDot={{
                r: 4,
                strokeWidth: 0,
                fill: activeTheme.upload.stroke,
              }}
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * RE-USABLE SUB-COMPONENTS
 */
const CustomTooltip = ({ active, payload, label, activeTheme }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-950/90 p-4 border border-gray-200 dark:border-white/10 shadow-2xl rounded-lg font-sans">
        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">
          {label}
        </p>
        {payload.map((p: any, index: number) => (
          <div key={index} className="flex items-center gap-6 my-2">
            <div className="flex items-center gap-2 min-w-[100px]">
              <div
                className="w-1.5 h-4 rounded-full"
                style={{ backgroundColor: p.color }}
              />
              <span className="text-sm font-bold text-gray-600 dark:text-gray-300">
                {p.name}
              </span>
            </div>
            <span
              className="font-mono font-bold text-base"
              style={{ color: p.color }}
            >
              {bpsToMbpsFormatter(p.value)}{' '}
              <span className="text-[10px] opacity-50">Mbps</span>
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-2 px-3 py-1">
    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-[10px] font-bold uppercase tracking-tighter text-gray-600 dark:text-gray-400">
      {label}
    </span>
  </div>
);

export default NetworkSpeedTestHistory;
